import { ArrowDropDown, InfoOutlined } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Typography,
    alpha,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getLocalCategoriesList,
    isCategoryUncategorized,
} from 'entities/Category';
import { getSelectedOrder } from 'entities/Order/model/selectors/ordersSelectors';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';

import { OrderGroupedItemsTable } from './OrderGroupedItemsTable';

interface OrderGroupedItemsTableProps {
    categoryId: string;
    items: CatalogItem[];
}

export const OrderGroupedItems = ({
    categoryId,
    items,
}: OrderGroupedItemsTableProps) => {
    const { t } = useTranslation();
    const categories = useSelector(getLocalCategoriesList);
    const currentOrder = useSelector(getSelectedOrder);

    const title = categories?.[categoryId]?.title;
    const glCode = categories?.[categoryId]?.glCode;
    const color = categories?.[categoryId]?.color;
    const isUncategorized = isCategoryUncategorized(title);

    return (
        <Box data-testclass="order-category">
            <Accordion
                defaultExpanded
                sx={{ boxShadow: 'none', borderRadius: '0px !important' }}
            >
                <AccordionSummary
                    sx={() => ({
                        backgroundColor: '#E9E8E8',
                        borderRadius: '0px',
                        padding: '14px 12px 14px 20px',
                    })}
                    expandIcon={
                        <ArrowDropDown
                            sx={(theme) => ({
                                color: alpha(theme.palette.common?.black, 0.3),
                            })}
                        />
                    }
                >
                    <Box display="flex" alignItems="center" gap="12px">
                        {!isUncategorized && (
                            <Box
                                width="8px"
                                height="8px"
                                borderRadius="50%"
                                overflow="hidden"
                                sx={{ backgroundColor: color }}
                            />
                        )}
                        <Box display={'flex'} alignItems="center" gap="8px">
                            <Box display="flex">
                                <Typography
                                    typography={'openSans.body2'}
                                    data-testclass="order-category-title"
                                    sx={{
                                        color: '#000',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        letterSpacing: '0',
                                        fontWeight: isUncategorized ? 600 : 400,
                                    }}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    sx={(theme) => ({
                                        color: alpha(
                                            theme.palette.common?.black,
                                            0.5,
                                        ),
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        letterSpacing: '0',
                                    })}
                                    typography={'openSans.body2'}
                                >
                                    {glCode ? ` | ${glCode}` : ''}
                                </Typography>
                            </Box>
                            {isUncategorized && (
                                <DynamicTooltip
                                    content={t(
                                        'Some items are not categorized, leading to inaccurate reporting.',
                                    )}
                                >
                                    <Box display="flex" alignItems="center">
                                        <InfoOutlined
                                            sx={{
                                                fontSize: '16px',
                                                color: '#FB8C00',
                                            }}
                                        />
                                    </Box>
                                </DynamicTooltip>
                            )}
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <OrderGroupedItemsTable
                        items={items}
                        orderStatus={currentOrder?.status}
                    />
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
